import { TYPES_THAT_SHOULD_BE_VIDEO, TYPE_MAPPING } from '@constants/fileTypes'
import type { FileTypeKeys } from '@constants/fileTypes'
import type { Asset } from '@/types'
import { toLowerCase } from '@utils/formats/index'
import {
  getTimestampFromDate,
  getParsedFilenameFromURL,
} from '@utils/getters/index'
import { fileTypeMapping } from './fileTypeMapping'
import appConfig from '@/config.js'

export type AssetInformation = {
  space?: string
  width: number
  height: number
  aspectRatio: number
  id?: string
  filename?: string
}

/**
 * @function isCurriedFn
 * @param {'images'|'videos'|'audios'|'texts'} typeMapping
 * @returns {Function}
 */
export const isCurriedFn =
  (
    typeMapping: keyof typeof TYPE_MAPPING | 'all',
  ): ((contentType: string) => boolean) =>
  (contentType: string): boolean => {
    let arrayToSearch: string[]
    if (typeMapping === 'all') {
      arrayToSearch = Object.values(TYPE_MAPPING).flat()
    } else {
      arrayToSearch = TYPE_MAPPING[typeMapping]
    }
    return arrayToSearch.some(
      (typeMappingEntry: string) =>
        toLowerCase(contentType).indexOf(typeMappingEntry.replace('*', '')) !==
        -1,
    )
  }

/**
 * @function isContentTypeImage
 * @param  {String} contentType
 * @return {Boolean}
 */
export const isContentTypeImage = isCurriedFn('images')

/**
 * @function isContentTypeEditableImage
 * @param  {String} contentType
 * @return {Boolean}
 */
export const isContentTypeEditableImage = isCurriedFn('editableImages')

/**
 * @function isContentTypeDocument
 * @param  {String} contentType
 * @return {Boolean}
 */
export const isContentTypeDocument = isCurriedFn('texts')

/**
 * @function isContentTypeVideo
 * @param  {String} contentType
 * @return {Boolean}
 */
export const isContentTypeVideo: (contentType: string) => boolean =
  isCurriedFn('videos')

/**
 * @function isContentTypeAudio
 * @param  {String} contentType
 * @return {Boolean}
 */
export const isContentTypeAudio = (contentType: string): boolean => {
  if (TYPES_THAT_SHOULD_BE_VIDEO.includes(contentType)) {
    return false
  }

  const value = toLowerCase(contentType)
  return TYPE_MAPPING.audios.some(
    (audioType) => value && value.indexOf(audioType.replace('*', '')) !== -1,
  )
}

/**
 * Checks if the provided value is an array of FileTypeKeys or a single string.
 * @param {string | string[]} value
 * @returns {boolean}
 */
function isFileTypeKeys(value: string | string[]): value is FileTypeKeys[] {
  const keys = Array.isArray(value) ? value : [value]
  return keys.every((k) => k in TYPE_MAPPING)
}

/**
 * @typedef {Object} assetObject
 * @property {string} filename
 * @property {string} content_type
 *
 * @function isAssetContentTypeValid
 * @param  {assetObject} asset an asset object to check
 * @param  {Array<string>} enabledFileTypes a list of enabled file type
 * @return {boolean}
 *
 * @example
 * // returns true
 * isAssetContentTypeValid({"filename":"[full_path]/audio_test.mp3","content_type":"audio/mpeg"}, ['audios'])
 */
export const isAssetContentTypeValid = (
  asset: Asset,
  enabledFileTypes: string | string[],
): boolean => {
  const fileContentType = asset.content_type

  const contentTypes = fileTypeMapping(
    isFileTypeKeys(enabledFileTypes) ? enabledFileTypes : [],
  )

  const internalFunction = (contentTypeToCheck: string): boolean => {
    return contentTypes.some((contentType) => {
      if (TYPES_THAT_SHOULD_BE_VIDEO.includes(contentTypeToCheck)) {
        return enabledFileTypes.includes('videos')
      }

      return (
        // if content_type is valid
        contentTypeToCheck &&
        // check if the content_type contains the string from enabled
        // field types
        contentTypeToCheck.indexOf(contentType.replace('*', '')) !== -1
      )
    })
  }

  if (fileContentType === null) {
    // get the format from the name and checks
    const { format } = getParsedFilenameFromURL(asset.filename)
    return internalFunction(format)
  }

  return internalFunction(fileContentType)
}

export const getAssetInformationFromUrl = (
  url: string,
): AssetInformation | undefined => {
  const data = removeSBAssetInformationFromURL(removeS3InformationFromURL(url))

  if (!data) {
    return
  }

  const [space, sizes, id, filename] = data.split('/')
  const [width = 0, height = 0] = sizes?.split('x')?.map(Number) || []

  return {
    space,
    width,
    height,
    // eslint-disable-next-line @storyblok/no-unsafe-parsing
    aspectRatio: parseFloat((width / height).toFixed(2)),
    id,
    filename,
  }
}

export const getExpectedSize = (
  width: number,
  height: number,
  expectedWidth: number,
  expectedHeight: number,
): string => {
  if (!width || !height) {
    return `${expectedWidth}x0`
  }

  if (height > width) {
    const size = height <= expectedHeight ? height : expectedHeight
    return `0x${size}`
  }

  const size = width <= expectedWidth ? width : expectedWidth
  return `${size}x0`
}

export const getResizedCloudinaryImageFromUrl = (
  url = '',
  expectedWidth: number,
): string => {
  const data = url.split('/image/upload/')

  return `${data[0]}/image/upload/c_scale,w_${expectedWidth}/${data[1]}`
}

type GetResizedImageFromUrlAsset = {
  url: string
  updatedAt?: string
}

type GetResizedImageFromUrlConfig = {
  width: number
  height?: number
  disableTimestamp?: boolean
  ignoreImageService?: boolean
}

export const getResizedImageFromUrl = (
  asset: GetResizedImageFromUrlAsset,
  config: GetResizedImageFromUrlConfig,
): string => {
  const { url, updatedAt } = asset
  const {
    width: expectedWidth,
    height: expectedHeight,
    disableTimestamp,
  } = config

  if (!url || !expectedWidth) {
    return ''
  }

  const cloudinaryPattern = /^(?:http|https):\/\/res\.cloudinary\.com/
  const isFromCloudinary = cloudinaryPattern.test(url)
  const cv = updatedAt ? getTimestampFromDate(updatedAt) : Date.now()

  if (
    (!isFromCloudinary && !isURLFromStoryblok(url)) ||
    appConfig.data.isBringYourOwnCloud()
  ) {
    return `${url}?cv=${cv}`
  }

  if (isFromCloudinary) {
    return getResizedCloudinaryImageFromUrl(url, expectedWidth)
  }

  if (url.endsWith('.svg')) {
    return `${url}?cv=${cv}`
  }

  const { width, height } = getAssetInformationFromUrl(url) || {}

  const replaced = removeS3InformationFromURL(url)

  if (config.ignoreImageService) {
    return `${replaced}?cv=${cv}`
  }

  const size = getExpectedSize(
    width ?? 0,
    height ?? 0,
    expectedWidth,
    expectedHeight ?? 0,
  )

  if (disableTimestamp) {
    return `${replaced}/m/${size}`
  }

  return `${replaced}/m/${size}?cv=${cv}`
}

export const getAssetBasePath = (filename: string): string => {
  const bucketSeparator = appConfig.data.isBringYourOwnCloud() ? '.' : '/'

  return removeSBAssetBucketFromURL(
    removeS3InformationFromURL(
      `/${filename.replace(/https:\/\/|http:\/\//, '')}`,
    ),
    bucketSeparator,
  )
}

export const removeS3InformationFromURL = (url: string): string => {
  return url
    .replace('s3.amazonaws.com/', '')
    .replace('s3.ap-southeast-2.amazonaws.com/', '')
    .replace('s3.ca-central-1.amazonaws.com/', '')
    .replace('s3.cn-north-1.amazonaws.com.cn/', '')
}

export const removeSBAssetBucketFromURL = (
  url: string,
  separator: string,
): string => {
  return url
    .replace(`a-us.storyblok.com${separator}`, '')
    .replace(`a-ca.storyblok.com${separator}`, '')
    .replace(`a-ap.storyblok.com${separator}`, '')
    .replace(`storyblokcn${separator}`, '')
    .replace(`a.storyblok.com${separator}`, '')
}

export const removeSBAssetInformationFromURL = (url: string): string => {
  return url
    .replace('https://a.storyblok.com/f/', '')
    .replace('https://a-us.storyblok.com/f/', '')
    .replace('https://a-ca.storyblok.com/f/', '')
    .replace('https://a-ap.storyblok.com/f/', '')
}

export const isURLFromStoryblok = (url: string): boolean => {
  const regexList = [
    // europe
    /^(http|https):\/\/a\.storyblok\.com/,
    /^\/\/a\.storyblok\.com/,
    // US
    /^(http|https):\/\/a-us\.storyblok\.com/,
    /^\/\/a-us\.storyblok\.com/,
    // CA
    /^(http|https):\/\/a-ca\.storyblok\.com/,
    /^\/\/a-ca\.storyblok\.com/,
    // AP
    /^(http|https):\/\/a-ap\.storyblok\.com/,
    /^\/\/a-ap\.storyblok\.com/,
    // S3
    /^(http|https):\/\/s3\.amazonaws\.com/,
    /^(http|https):\/\/s3\.ca-central-1\.amazonaws\.com/,
    /^(http|https):\/\/s3\.ap-southeast-2\.amazonaws\.com/,
  ]

  return regexList.some((regex) => regex.test(url))
}

/**
 * Extracts the filename from a URL
 * @param src - The URL to extract the filename from
 * @returns The extracted filename or undefined if no filename could be found
 *
 * @example
 * getFilenameFromUrl('https://s3.amazonaws.com/f/110558/content-selected.png?cv=123') // Returns 'content-selected.png'
 * getFilenameFromUrl('https://example.com/path/') // Returns undefined
 */
export const getFilenameFromUrl = (src: string): string | undefined => {
  try {
    const url = new URL(src)
    const segments = url.pathname.split('/')
    const lastSegment = segments[segments.length - 1]

    if (!lastSegment) {
      return undefined
    }

    return decodeURIComponent(lastSegment)
  } catch {
    return undefined
  }
}
